"use client";

import { useState } from "react";
import {
  type CipherMode,
  CipherWorkbench,
} from "@/components/tool-kit/cipher-workbench";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, LABEL, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  applyTextOptions,
  type CipherTextOptions,
  groupLetters,
} from "@/lib/tool-kit/ciphers/alphabet";
import {
  type ColumnarOptions,
  columnarDecode,
  columnarEncode,
  columnLengths,
  keyUsable,
  MAX_KEY_LENGTH,
  readingOrder,
  type TranspositionGrid,
} from "@/lib/tool-kit/ciphers/columnar";

const ROW_LIMIT = 24;
const GROUP_SIZE = 5;
const FILLER = "X";

/** Letters only, in capitals: the classic way to send a transposition. */
const LETTERS_ONLY: CipherTextOptions = {
  keepCase: false,
  keepSpaces: false,
  keepPunctuation: false,
};

type Padding = "none" | "filler";

/** English strings. A translated content file overrides these through `ui`. */
export const COLUMNAR_TRANSPOSITION_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Plain text",
  cipherText: "Cipher text",
  decodedText: "Decoded text",
  inputPlaceholder: "Type or paste your message",
  sample: "We are discovered. Flee at once.",
  keyword: "Keyword",
  keywordSample: "ZEBRAS",
  keywordHint:
    "One column per letter, read in alphabetical order of the key; a repeated letter is numbered left to right. Digits work too (3142).",
  keyProblem: "The key needs between 2 and {max} letters or digits.",
  padding: "The last row",
  padNone: "Leave it short (irregular columns)",
  padFiller: "Fill it with X (complete rectangle)",
  double: "Double transposition — put the result through a second grid",
  secondKeyword: "Second keyword",
  secondKeywordSample: "STRIPE",
  secondKeywordHint:
    "Can be the same as the first. Padding, if any, completes the first grid only.",
  group: "Write the cipher text in groups of five",
  keep: "Move these too (off: letters only, in capitals)",
  keepCase: "Lower case",
  keepSpaces: "Spaces",
  keepPunctuation: "Punctuation and numbers",
  keepNote:
    "Decode with the same choices you encoded with — every character kept here is part of the grid.",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  outputPlaceholder: "The result appears here.",
  fillerNote:
    "The X letters added to complete the grid come back at the end of the decoded text — delete them.",
  working: "The grids",
  noText: "Type a message to see the grid.",
  gridEncodeFirst: "Grid 1: write the message in rows under {key}",
  gridEncodeSecond: "Grid 2: write the first result in rows under {key}",
  gridDecodeSecond: "Undo grid 2: fill the columns of {key} in number order",
  gridDecodeFirst: "Undo grid 1: fill the columns of {key} in number order",
  gridLabel: "Transposition grid for the key {key}",
  orderRow: "Order",
  readColumns: "Read the columns in order:",
  readRows: "Read the rows:",
  columnLengths:
    "Column lengths, left to right: {lengths}. With no padding the last row is short, so the leftmost columns hold one character more — the receiver works this out from the length alone.",
  fillerCells: "one: {count} filler letter|other: {count} filler letters",
  rowLimit:
    "one: Showing the first row.|other: Showing the first {count} rows.",
} as const;

type Strings = typeof COLUMNAR_TRANSPOSITION_CIPHER_STRINGS;

/** Each column's characters, in reading order: what gets written out. */
function columnTexts(grid: TranspositionGrid): string[] {
  return readingOrder(grid.ranks).map((column) =>
    grid.rows
      .map((row) => row[column])
      .filter((cell) => cell !== null)
      .join("")
  );
}

function GridTable({
  grid,
  strings,
}: {
  grid: TranspositionGrid;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const keyText = grid.key.join("");
  const rows = grid.rows.slice(0, ROW_LIMIT);
  const cellCount = grid.rows.length * grid.key.length;
  const fillerStart = cellCount - grid.fillers;
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto">
        <table
          aria-label={t(strings.gridLabel, { key: keyText })}
          className="border-separate border-spacing-1 font-mono"
        >
          <thead>
            <tr>
              {grid.key.map((letter, column) => (
                <th
                  className="h-9 w-9 rounded bg-primary/10 text-center font-semibold text-foreground"
                  // biome-ignore lint/suspicious/noArrayIndexKey: key letters repeat
                  key={column}
                  scope="col"
                >
                  {letter}
                </th>
              ))}
            </tr>
            <tr>
              {grid.ranks.map((rank, column) => (
                <td
                  className="text-center text-muted-foreground text-xs"
                  // biome-ignore lint/suspicious/noArrayIndexKey: columns are positional
                  key={column}
                >
                  <span className="sr-only">{strings.orderRow} </span>
                  {fmt(rank + 1)}
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional
              <tr key={rowIndex}>
                {row.map((cell, column) => {
                  const filler =
                    rowIndex * grid.key.length + column >= fillerStart;
                  let tone = "border-border bg-card text-foreground";
                  if (cell === null) {
                    tone = "border-transparent";
                  } else if (filler) {
                    tone =
                      "border-amber-500 bg-amber-200/60 text-foreground dark:bg-amber-400/25";
                  }
                  return (
                    <td
                      className={`h-9 w-9 rounded border text-center ${tone}`}
                      // biome-ignore lint/suspicious/noArrayIndexKey: cells are positional
                      key={column}
                    >
                      {cell === " " ? "␣" : cell}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {grid.fillers > 0 ? (
        <p className={HINT}>
          {tPlural(strings.fillerCells, locale, grid.fillers, {
            count: fmt(grid.fillers),
          })}
        </p>
      ) : null}
      {grid.rows.length > ROW_LIMIT ? (
        <p className={HINT}>
          {tPlural(strings.rowLimit, locale, ROW_LIMIT, {
            count: fmt(ROW_LIMIT),
          })}
        </p>
      ) : null}
    </div>
  );
}

function GridStep({
  title,
  grid,
  reading,
  strings,
}: {
  title: string;
  grid: TranspositionGrid;
  reading: "columns" | "rows";
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const lengths = columnLengths(
    grid.rows.reduce(
      (sum, row) => sum + row.filter((cell) => cell !== null).length,
      0
    ),
    grid.key.length
  );
  const irregular = new Set(lengths).size > 1;
  const text =
    reading === "columns"
      ? columnTexts(grid).join(" · ")
      : grid.rows
          .flat()
          .filter((cell) => cell !== null)
          .join("");
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{title}</h3>
      <GridTable grid={grid} strings={strings} />
      {irregular && reading === "rows" ? (
        <p className={HINT}>
          {t(strings.columnLengths, {
            lengths: lengths.map((length) => fmt(length)).join(" "),
          })}
        </p>
      ) : null}
      <p className="break-all text-sm">
        <span className={LABEL}>
          {reading === "columns" ? strings.readColumns : strings.readRows}
        </span>{" "}
        <span className="font-mono text-foreground">{text}</span>
      </p>
    </div>
  );
}

function Working({
  mode,
  grids,
  strings,
}: {
  mode: CipherMode;
  grids: readonly TranspositionGrid[];
  strings: Strings;
}) {
  if (grids.length === 0 || grids[0].rows.length === 0) {
    return <Notice>{strings.noText}</Notice>;
  }
  const [first, second] = grids;
  const keyOf = (grid: TranspositionGrid) => grid.key.join("");
  if (mode === "encode") {
    return (
      <>
        <GridStep
          grid={first}
          reading="columns"
          strings={strings}
          title={t(strings.gridEncodeFirst, { key: keyOf(first) })}
        />
        {second ? (
          <GridStep
            grid={second}
            reading="columns"
            strings={strings}
            title={t(strings.gridEncodeSecond, { key: keyOf(second) })}
          />
        ) : null}
      </>
    );
  }
  return (
    <>
      {second ? (
        <GridStep
          grid={second}
          reading="rows"
          strings={strings}
          title={t(strings.gridDecodeSecond, { key: keyOf(second) })}
        />
      ) : null}
      <GridStep
        grid={first}
        reading="rows"
        strings={strings}
        title={t(strings.gridDecodeFirst, { key: keyOf(first) })}
      />
    </>
  );
}

export default function ColumnarTranspositionCipherWidget() {
  const strings = useToolUi(COLUMNAR_TRANSPOSITION_CIPHER_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [keyword, setKeyword] = useState<string>(strings.keywordSample);
  const [padding, setPadding] = useState<Padding>("none");
  const [double, setDouble] = useState(false);
  const [secondKeyword, setSecondKeyword] = useState<string>(
    strings.secondKeywordSample
  );
  const [group, setGroup] = useState(true);
  const [options, setOptions] = useState(LETTERS_ONLY);

  const keysUsable =
    keyUsable(keyword) && (!double || keyUsable(secondKeyword));
  const settings: ColumnarOptions = {
    key: keyword,
    secondKey: double ? secondKeyword : null,
    filler: padding === "filler" ? FILLER : "",
  };
  const prepared = applyTextOptions(input, options);
  let result: ReturnType<typeof columnarEncode> | null = null;
  if (keysUsable) {
    result =
      mode === "encode"
        ? columnarEncode(prepared, settings)
        : columnarDecode(prepared, settings);
  }
  const raw = result?.output ?? "";
  const output =
    mode === "encode" && group ? groupLetters(raw, GROUP_SIZE) : raw;

  const keyControls = (
    <div className="space-y-4">
      <TextField
        hint={strings.keywordHint}
        id="ct-key"
        label={strings.keyword}
        mono
        onChange={setKeyword}
        value={keyword}
      />
      <PillGroup
        label={strings.padding}
        name="ct-padding"
        onChange={setPadding}
        options={[
          { value: "none", label: strings.padNone },
          { value: "filler", label: strings.padFiller },
        ]}
        value={padding}
      />
      <Checkbox checked={double} label={strings.double} onChange={setDouble} />
      {double ? (
        <TextField
          hint={strings.secondKeywordHint}
          id="ct-key-2"
          label={strings.secondKeyword}
          mono
          onChange={setSecondKeyword}
          value={secondKeyword}
        />
      ) : null}
      {mode === "encode" ? (
        <Checkbox checked={group} label={strings.group} onChange={setGroup} />
      ) : null}
    </div>
  );

  return (
    <CipherWorkbench
      idPrefix="ct"
      input={input}
      keyControls={keyControls}
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: mode === "encode" ? strings.plainText : strings.cipherText,
        inputPlaceholder: strings.inputPlaceholder,
        output: mode === "encode" ? strings.cipherText : strings.decodedText,
        outputPlaceholder: strings.outputPlaceholder,
        textOptions: strings.keep,
        keepCase: strings.keepCase,
        keepSpaces: strings.keepSpaces,
        keepPunctuation: strings.keepPunctuation,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={
        <>
          <p className={HINT}>{strings.keepNote}</p>
          {keysUsable ? null : (
            <ErrorNotice>
              {t(strings.keyProblem, { max: fmt(MAX_KEY_LENGTH) })}
            </ErrorNotice>
          )}
        </>
      }
      onInputChange={setInput}
      onModeChange={setMode}
      output={output}
      outputNotes={
        mode === "decode" && padding === "filler" && raw !== "" ? (
          <p className={HINT}>{strings.fillerNote}</p>
        ) : null
      }
      textOptions={{
        value: options,
        onChange: setOptions,
        show: ["keepCase", "keepSpaces", "keepPunctuation"],
      }}
      working={
        result ? (
          <Working grids={result.grids} mode={mode} strings={strings} />
        ) : null
      }
    />
  );
}
